/**
 * Canonical MessageInstanceEventClient implementation.
 *
 * This is the source of truth for frontend in-app message instance events.
 * `@atlassian/message-instance-event-frontend-client/message-instance-event-client` and the legacy
 * `@post-office/message-instance-event-frontend-client` package both export this implementation.
 */

export type AdditionalMetadata = Record<string, string | number | boolean | null> & {
	transactionAccount?: string;
	tenantId?: string;
};

type InAppPullContext = {
	// PostOfficeContextValue fields
	product?: string;
	locale?: string;
	tenantId?: string;
	orgId?: string;
	subproduct?: string;
	isSiteAdmin?: boolean;
	workspaceId?: string;
	// Required for pull context
	source: string;
	recommendation: {
		recommendationSessionId: string;
		recommendationEntityId: string;
	};
};

type InAppMessageInstanceEventBase = {
	messageInstanceId: string;
	messageTemplateId: string;
	deliveryChannel: 'in-app';
	placementId: string;
	channelType: 'pull';
	context: InAppPullContext;
	messageCreationType: 'implicit' | 'explicit';
	messageTriggerId: string;
	journeyId?: string;
	journeyTriggerId?: string;
	variant?: string | number | boolean | null;
	configSetId?: string;
	origin?: {
		originId: string;
		originProduct: string;
	};
	timestamp: number;
};

export type ClickedElementAttributes =
	| ClickedElementAttributesButton
	| ClickedElementAttributesOther;

export type ClickedElementAttributesButton = {
	/** A human-readable camel-cased string to identify the click, for example: learnMore, tryItNow. */
	clickIdentifier: string;
	clickedElement: 'button';
	buttonType: 'primary' | 'secondary';
};

export type ClickedElementAttributesOther = {
	/** A human-readable camel-cased string to identify the click, for example: learnMore, tryItNow. */
	clickIdentifier: string;
	clickedElement: 'link' | 'dropdown-menu' | 'cross-icon';
};

export type ActionTypeAttributes =
	| ActionTypeAttributesOpen
	| ActionTypeAttributesOther
	| ActionTypeAttributesSnooze
	| ActionTypeAttributesSteps
	| ActionTypeAttributesRead
	| ActionTypeAttributesUnread;

export type ActionTypeAttributesOpen = {
	actionType: 'open';
	destinationUrl?: string;
	trackingLinkId?: string;
};

export type ActionTypeAttributesSnooze = {
	actionType: 'snooze';
	snoozeDuration: number;
};

export type ActionTypeAttributesSteps = {
	actionType: 'next' | 'previous';
	currentStep: number;
	previousStep: number;
	stepName?: string;
};

export type ActionTypeAttributesOther = {
	actionType: 'acknowledge' | 'dismiss' | 'react';
};

export type ActionTypeAttributesRead = {
	actionType: 'read';
};

export type ActionTypeAttributesUnread = {
	actionType: 'unread';
};

export type InAppViewedEvent = InAppMessageInstanceEventBase & {
	messageEventType: 'viewed';
	additionalMetadataFromFrontend?: AdditionalMetadata | null;
};

export type InAppDeliverySuccessfulEvent = InAppMessageInstanceEventBase & {
	messageEventType: 'deliverySuccessful';
	additionalMetadataFromFrontend?: AdditionalMetadata | null;
};

export type InAppClickedEvent = InAppMessageInstanceEventBase & {
	messageEventType: 'clicked';
	clickedElement: ClickedElementAttributes;
	action: ActionTypeAttributes;
	additionalMetadataFromFrontend?: AdditionalMetadata | null;
};

export type FrontendMessageInstanceEventWithoutRecipient =
	| InAppViewedEvent
	| InAppDeliverySuccessfulEvent
	| InAppClickedEvent;

export type MessageInstanceClickedParams = Omit<
	InAppClickedEvent,
	keyof Omit<
		InAppMessageInstanceEventBase,
		'additionalMetadataFromFrontend' | 'messageEventType'
	>
> & {
	messageInstanceId?: string;
	messageTemplateId?: string;
	messageTriggerId?: string;
	messageCreationType?: 'implicit' | 'explicit';
	additionalMetadataFromFrontend?: AdditionalMetadata;
};

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

const DEFAULT_PATH = `/gateway/api/post-office/api/v1/message-instance-event/bulk`;
const FLUSH_BATCH_EVENTS_TIMEOUT = 1000;
const MESSAGE_EVENTS_BATCH_SIZE = 30;

if (typeof fetch === 'undefined') {
	// If fetch is not supported, load the polyfill.
	require('whatwg-fetch');
}

export class MessageInstanceEventClient {
	private readonly endpointUrl: string = DEFAULT_PATH;
	private scheduledTimeout: number | null = null;
	private batchEvents: FrontendMessageInstanceEventWithoutRecipient[] = [];

	public async sendMessageInstanceEvent(
		event: FrontendMessageInstanceEventWithoutRecipient,
	): Promise<void> {
		this.batchEvents.push(event);
		if (this.scheduledTimeout === null) {
			this.scheduledTimeout = globalThis.window.setTimeout(
				this.flushBatchEvents.bind(this),
				FLUSH_BATCH_EVENTS_TIMEOUT,
			);
		}
	}

	private async sendEventsToBackend(events: FrontendMessageInstanceEventWithoutRecipient[]) {
		const url = `${this.endpointUrl}`;
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(events),
		});
		if (!response.ok) {
			// eslint-disable-next-line no-console
			console.error(`Failed to send event to analytics endpoint: ${response.statusText}`);
		}
	}

	private async flushBatchEvents() {
		if (this.batchEvents.length === 0) {
			return;
		}

		if (this.batchEvents.length > MESSAGE_EVENTS_BATCH_SIZE) {
			const batch = this.batchEvents.slice(0, MESSAGE_EVENTS_BATCH_SIZE);
			await this.sendEventsToBackend(batch);
			if (this.scheduledTimeout !== null) {
				globalThis.window.clearTimeout(this.scheduledTimeout);
			}
			this.batchEvents = this.batchEvents.slice(MESSAGE_EVENTS_BATCH_SIZE, this.batchEvents.length);
			this.scheduledTimeout = globalThis.window.setTimeout(
				this.flushBatchEvents.bind(this),
				FLUSH_BATCH_EVENTS_TIMEOUT,
			);
			return;
		}

		await this.sendEventsToBackend(this.batchEvents);
		this.batchEvents = [];

		if (this.scheduledTimeout !== null) {
			globalThis.window.clearTimeout(this.scheduledTimeout);
			this.scheduledTimeout = null;
		}
	}
}

declare global {
	interface Window {
		postOfficeMessageInstanceEventClient?: MessageInstanceEventClient;
	}
}
